import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import Navbar from './Navbar';
import AuthContext from '../context/AuthContext';
import { API_BASE_URL } from '../config/adminConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const formatDay = dayKey => {
  if (!dayKey) {
    return 'Unknown';
  }

  const date = new Date(`${dayKey}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return dayKey;
  }

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

function FeedInteractions() {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/api/admin/feed-interactions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setData(response.data);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          'Unable to load feed engagement summary.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const summary = data?.summary || {};
  const topContent = Array.isArray(data?.topContent) ? data.topContent : [];
  const dailyTrend = Array.isArray(data?.dailyTrend) ? data.dailyTrend : [];

  const trendData = {
    labels: dailyTrend.map(item => formatDay(item.day)),
    datasets: [
      {
        label: 'Opens',
        data: dailyTrend.map(item => item.opens || 0),
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.14)',
        tension: 0.3,
      },
      {
        label: 'Completions',
        data: dailyTrend.map(item => item.completions || 0),
        borderColor: '#0F766E',
        backgroundColor: 'rgba(15, 118, 110, 0.14)',
        tension: 0.3,
      },
    ],
  };

  const topContentData = {
    labels: topContent.map(item => item.title),
    datasets: [
      {
        label: 'Opens',
        data: topContent.map(item => item.openEvents || 0),
        backgroundColor: 'rgba(37, 99, 235, 0.74)',
      },
      {
        label: 'Completions',
        data: topContent.map(item => item.completeEvents || 0),
        backgroundColor: 'rgba(15, 118, 110, 0.74)',
      },
    ],
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Navbar />
        <Box display="flex" justifyContent="center" py={20}>
          <Spinner size="xl" />
        </Box>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Navbar />
      <Box maxW="7xl" mx="auto" px={6} py={6}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', md: 'center' }}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={4}
          mb={8}
        >
          <Box>
            <Heading size="xl" mb={2}>
              Feed Engagement
            </Heading>
            <Text color="gray.600" maxW="3xl">
              Premium content usage, reading time and video completion patterns
              across the B2C app.
            </Text>
          </Box>
          <Button colorScheme="blue" onClick={fetchSummary}>
            Refresh
          </Button>
        </Box>

        {error ? (
          <Card shadow="sm" mb={6}>
            <CardBody>
              <Text fontWeight="700" mb={2}>
                Feed engagement unavailable
              </Text>
              <Text color="gray.600">{error}</Text>
            </CardBody>
          </Card>
        ) : null}

        <SimpleGrid columns={[1, 2, 3, 6]} spacing={4} mb={8}>
          <MetricCard
            label="Tracked Events"
            value={summary.totalEvents || 0}
            help="Open, progress, complete and close"
            tone="blue"
          />
          <MetricCard
            label="Sessions"
            value={summary.totalSessions || 0}
            help="Distinct feed sessions"
            tone="purple"
          />
          <MetricCard
            label="Unique Viewers"
            value={summary.uniqueViewers || 0}
            help="Premium users with activity"
            tone="green"
          />
          <MetricCard
            label="Open Events"
            value={summary.openEvents || 0}
            help="Content starts"
            tone="orange"
          />
          <MetricCard
            label="Completion Rate"
            value={`${summary.completionRate || 0}%`}
            help="Completed vs opened sessions"
            tone="teal"
          />
          <MetricCard
            label="Avg Time"
            value={`${summary.averageEngagedSeconds || 0}s`}
            help="Average engaged time"
            tone="gray"
          />
        </SimpleGrid>

        <SimpleGrid columns={[1, 1, 2]} spacing={6} mb={6}>
          <Card shadow="sm">
            <CardHeader pb={2}>
              <Heading size="md">Daily Engagement Trend</Heading>
            </CardHeader>
            <CardBody h="320px">
              {dailyTrend.length ? (
                <Line
                  data={trendData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              ) : (
                <Text color="gray.500">No feed activity tracked yet.</Text>
              )}
            </CardBody>
          </Card>

          <Card shadow="sm">
            <CardHeader pb={2}>
              <Heading size="md">Top Content by Opens</Heading>
            </CardHeader>
            <CardBody h="320px">
              {topContent.length ? (
                <Bar
                  data={topContentData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                  }}
                />
              ) : (
                <Text color="gray.500">No content engagement available yet.</Text>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        <Card shadow="sm">
          <CardHeader pb={2}>
            <Heading size="md">Content Performance Table</Heading>
          </CardHeader>
          <CardBody overflowX="auto">
            {topContent.length ? (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Content</Th>
                    <Th>Type</Th>
                    <Th isNumeric>Opens</Th>
                    <Th isNumeric>Unique Viewers</Th>
                    <Th isNumeric>Completion Rate</Th>
                    <Th isNumeric>Avg Time</Th>
                    <Th isNumeric>Avg Progress</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {topContent.map(item => (
                    <Tr key={item.contentId}>
                      <Td>{item.title}</Td>
                      <Td>
                        <Badge colorScheme={item.type === 'video' ? 'purple' : 'blue'}>
                          {item.type}
                        </Badge>
                      </Td>
                      <Td isNumeric>{item.openEvents}</Td>
                      <Td isNumeric>{item.uniqueViewers}</Td>
                      <Td isNumeric>{item.completionRate}%</Td>
                      <Td isNumeric>{item.averageEngagedSeconds}s</Td>
                      <Td isNumeric>{item.averageProgressPercent}%</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text color="gray.500">No feed engagement data available yet.</Text>
            )}
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
}

function MetricCard({ label, value, help, tone = 'gray' }) {
  const tones = {
    blue: 'blue.500',
    purple: 'purple.500',
    green: 'green.500',
    orange: 'orange.500',
    teal: 'teal.500',
    gray: 'gray.700',
  };

  return (
    <Card shadow="sm">
      <CardBody>
        <Stat>
          <StatLabel>{label}</StatLabel>
          <StatNumber color={tones[tone] || tones.gray}>{value}</StatNumber>
          <StatHelpText mb={0}>{help}</StatHelpText>
        </Stat>
      </CardBody>
    </Card>
  );
}

export default FeedInteractions;
